import React, { useState, useEffect } from 'react';
import { Route, Switch, useLocation, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './ServiceManagement.scss';
import { formatAccountType, getSearchParamValue, getErrorMessage } from '../../utils';
import { GlobalRouteNames, ModalVariants, LocalStorageItems, QueryParams } from '../../models/Common';
import { userInfoApi } from '../../services/apiServices/user/userInfo';
import { UserSubtitle } from '../../components/commons/UserSubtitle';
import { IptvDevices } from '../../components/serviceManagementsParts/maine/IptvDevices';
import { BrizTvInfo } from '../../components/serviceManagementsParts/maine/BrizTvInfo';
import { InternetDevice } from '../../components/serviceManagementsParts/maine/InternetDevice';
import { GeneralInfo } from '../../components/serviceManagementsParts/maine/GeneralInfo';
import { Sessions } from '../../components/serviceManagementsParts/Sessions';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { Page404 } from '../404';
import { useUserAddress } from '../../hooks/useUserAddress';
import { useAccountInfo } from '../../hooks/useAccountInfo';
import { modalSlice } from '../../store/reducers/ModalSlice';
import { initialState } from '../../store/reducers/UserInfoSlice';
import { ConnectionProtocolTypes } from '../../models/Accounts';

export interface InfoItem {
  label?: string | JSX.Element;
  value?: string | JSX.Element | number;
  button?: JSX.Element;
  className?: string;
  alignCenter?: boolean;
  buttonShowPassword?: JSX.Element;
}

export interface IptvPermission {
  canAdd: boolean;
  canEdit: boolean;
  canRemove: boolean;
}

export const ServiceManagement = () => {
  const { t } = useTranslation();
  const { openModal } = modalSlice.actions;
  const { selectedLanguage: selectedLang } = useAppSelector((state) => state.langReducer);
  const dispatch = useAppDispatch();
  const { search } = useLocation();
  const history = useHistory();
  const accountType = formatAccountType(getSearchParamValue(QueryParams.AccountType, search));
  const accountId = +getSearchParamValue(QueryParams.AccountId, search);
  const { accountInfo, isError, error } = useAccountInfo(accountId, accountType);
  const [isBrizTvPasswordVisible, setIsBrizTvPasswordVisible] = useState(false);
  const [isInternetPasswordVisible, setIsInternetPasswordVisible] = useState(false);
  const controlledAccount = localStorage.getItem(LocalStorageItems.ControlledAccount);
  const { useGetUserInfoQuery, useGetUserInfoMultiAccQuery } = userInfoApi;
  const { data: userInfoState } = controlledAccount
    ? useGetUserInfoMultiAccQuery(
        { subAccountId: JSON.parse(controlledAccount).UserID },
        { refetchOnMountOrArgChange: true },
      )
    : useGetUserInfoQuery('', { refetchOnMountOrArgChange: true });
  const { userAddress } = useUserAddress(userInfoState || initialState.userInfoState, selectedLang);
  const isEditingInternetAvailable = userInfoState?.Permissions.Services.Internet.CanEdit;

  const iptvPermissions: IptvPermission = {
    canAdd: !!userInfoState?.Permissions.Services.Iptv.CanAdd,
    canEdit: !!userInfoState?.Permissions.Services.Iptv.CanEdit,
    canRemove: !!userInfoState?.Permissions.Services.Iptv.CanRemove,
  };

  useEffect(() => {
    if (isError && error) {
      const message = getErrorMessage(error);
      history.replace({ pathname: GlobalRouteNames.UnknownError, state: { message } });
    }
  }, [isError, error]);

  const handleEditInternetLogin = (
    login: string,
    id: number,
    connectionProtocolId: number,
    selectedLanguage: string,
  ) => {
    dispatch(
      openModal({
        modalVariants: ModalVariants.EditInternetLogin,
        content: { login, accountId: id, connectionProtocolId, selectedLanguage },
      }),
    );
  };

  const handleEditInternetMacAddress = (
    macAddress: string,
    id: number,
    connectionProtocolId: number,
    selectedLanguage: string,
  ) => {
    dispatch(
      openModal({
        modalVariants: ModalVariants.EditMacAddress,
        content: { accountId: id, macAddress, connectionProtocolId, selectedLanguage },
      }),
    );
  };

  const handleEditInternetPassword = (id: number, connectionProtocolId: number, selectedLanguage: string) => {
    dispatch(
      openModal({
        modalVariants: ModalVariants.EditInternetPassword,
        content: { accountId: id, connectionProtocolId, selectedLanguage },
      }),
    );
  };

  const handleEditIptvDevice = (id: number, mac: string, deviceId: number, login: string) => {
    dispatch(
      openModal({
        modalVariants: ModalVariants.EditIptvDevice,
        content: { accountId: id, macAddress: mac, deviceId, login },
      }),
    );
  };

  const handleAddNewIptvDevice = (id: number) => {
    dispatch(
      openModal({
        modalVariants: ModalVariants.AddNewIptvDevice,
        content: { accountId: id },
      }),
    );
  };

  const handleDeleteIptvDevice = (id: number, deviceId: number, macAddress: string) => {
    dispatch(
      openModal({
        modalVariants: ModalVariants.DeleteIptvDevice,
        content: { accountId: id, deviceId, macAddress },
      }),
    );
  };

  const handleEditBrizTvEmail = (id: number | string) => {
    dispatch(
      openModal({
        modalVariants: ModalVariants.EditBrizTvEmail,
        content: { accountId: id },
      }),
    );
  };

  const handleEditBrizTvPhone = (id: number | string) => {
    dispatch(
      openModal({
        modalVariants: ModalVariants.EditBrizTvPhone,
        content: { accountId: id },
      }),
    );
  };

  const handleEditBrizTvPassword = (id: number | string) => {
    dispatch(
      openModal({
        modalVariants: ModalVariants.EditBrizTvPassword,
        content: { accountId: id },
      }),
    );
  };

  const handleShowBrizTvPassword = () => {
    setIsBrizTvPasswordVisible(!isBrizTvPasswordVisible);
  };

  const handleShowInternetPassword = () => {
    setIsInternetPasswordVisible(!isInternetPasswordVisible);
  };

  return (
    <Switch>
      <Route path={GlobalRouteNames.ServiceManagement} exact>
        <div className="service-management page">
          <div className="service-management__container container">
            <div className="service-management__maine card card__primary card__primary_full-mobile">
              {!controlledAccount ? (
                <h1 className="service-management__title">{t('serviceManagement.serviceManagement')}</h1>
              ) : (
                <h1 className="service-management__title">
                  {t('serviceManagement.serviceAccountManagement')}
                  <UserSubtitle
                    address={userAddress.trim()}
                    userId={userInfoState?.id ?? initialState.userInfoState.id}
                  />
                </h1>
              )}
              <div className="service-management__maine__content">
                <GeneralInfo
                  serviceName={accountInfo.serviceName}
                  connectionType={accountInfo.connectionType}
                  servicePrice={accountInfo.servicePrice}
                  isDaily={accountInfo.isDaily}
                />
                {accountInfo.isInternet && (
                  <InternetDevice
                    login={accountInfo.internetLogin}
                    handleEditLogin={handleEditInternetLogin}
                    password={accountInfo.internetPassword}
                    handleEditPassword={handleEditInternetPassword}
                    mac={accountInfo.internetMac}
                    handleEditMac={handleEditInternetMacAddress}
                    ip={accountInfo.internetIp}
                    accountId={accountInfo.internetId}
                    connectionProtocolId={accountInfo?.connectionProtocolId || ConnectionProtocolTypes.Default}
                    isEditingInternetAvailable={!isEditingInternetAvailable}
                    handleShowPassword={handleShowInternetPassword}
                    isShownPassword={isInternetPasswordVisible}
                  />
                )}
                {accountInfo.isBrizTv && (
                  <BrizTvInfo
                    iptvId={accountInfo.iptvId}
                    email={accountInfo.brizTvEmail}
                    handleChangeEmail={handleEditBrizTvEmail}
                    password={accountInfo.brizTvPassword}
                    handleChangePassword={handleEditBrizTvPassword}
                    phone={accountInfo.brizTvPhone}
                    handleChangePhone={handleEditBrizTvPhone}
                    isEditingBrizTvAvailable={!iptvPermissions.canEdit}
                    handleShowPassword={handleShowBrizTvPassword}
                    isShownPassword={isBrizTvPasswordVisible}
                  />
                )}
                {accountInfo.isIptv && !!accountInfo.iptvDevices.length && (
                  <IptvDevices
                    iptvDevices={accountInfo.iptvDevices}
                    iptvAccountId={accountInfo.iptvId}
                    handleAdd={handleAddNewIptvDevice}
                    handleEdit={handleEditIptvDevice}
                    handleDelete={handleDeleteIptvDevice}
                    iptvPermissions={iptvPermissions}
                  />
                )}
              </div>
            </div>
            {accountInfo.isInternet && (
              <div className="service-management__maine card card__primary card__primary_full-mobile">
                <Sessions
                  accountType={accountType}
                  accountId={accountInfo.internetId || accountId}
                  isUnion={accountInfo.isUnion}
                />
              </div>
            )}
          </div>
        </div>
      </Route>
      <Route component={Page404} path={GlobalRouteNames.Page404} />
    </Switch>
  );
};
